<template>
  <div ref="rootEl" class="filters">
    <div class="filter-dropdown" :class="{ open: categoryOpen }">
      <button type="button" class="filter-chip" @click="toggleCategory">
        {{ t('promo.records.filterCategory') }} ·
        <span class="chip-value">{{ categoryLabel }}</span>
        <span class="chev">▾</span>
      </button>
      <ul v-if="categoryOpen" class="filter-options">
        <li
          v-for="opt in categoryOptions"
          :key="opt.value"
          :class="{ active: opt.value === modelCategory }"
          @click="selectCategory(opt.value)"
        >
          {{ opt.label }}
        </li>
      </ul>
    </div>

    <div class="filter-dropdown" :class="{ open: timeOpen }">
      <button type="button" class="filter-chip" @click="toggleTime">
        {{ t('promo.records.filterTime') }} · <span class="chip-value">{{ timeLabel }}</span>
        <span class="chev">▾</span>
      </button>
      <ul v-if="timeOpen" class="filter-options">
        <li
          v-for="opt in timeOptions"
          :key="opt.value"
          :class="{ active: opt.value === modelTime }"
          @click="selectTime(opt.value)"
        >
          {{ opt.label }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type {
  RecordFilterModule,
  RecordFilterOption,
  RecordTimeOption,
  RecordTimeRange,
} from '../types'

const { t } = useI18n()
const { recordFilter } = useRewardSectionLists()

const rootEl = ref<HTMLElement | null>(null)

onClickOutside(rootEl, () => {
  categoryOpen.value = false
  timeOpen.value = false
})

const modelCategory = defineModel<RecordFilterModule>('category', { required: true })
const modelTime = defineModel<RecordTimeRange>('time', { required: true })

/** reward_record_filter symbol → 领取记录筛选值，name → 显示；不认识的 symbol 跳过 */
const SYMBOL_TO_FILTER: Record<string, RecordFilterModule> = {
  all: 'all',
  promo: 'promo',
  task: 'task',
  vip_salary: 'vip_salary',
  rakeback: 'rakeback',
  refer: 'referral',
}

const categoryOptions = computed<RecordFilterOption[]>(() => {
  const opts = recordFilter.value.flatMap(item => {
    const value = SYMBOL_TO_FILTER[item.symbol]
    return value ? [{ value, label: item.name }] : []
  })
  // 「全部」是保证存在的选中值；接口未下发时本地补一项，避免 label 解析为空
  if (!opts.some(o => o.value === 'all')) {
    opts.unshift({ value: 'all', label: t('promo.records.category.all') })
  }
  return opts
})

const timeOptions = computed<RecordTimeOption[]>(() => [
  { value: 'today', label: t('common.time.range.today') },
  { value: 'yesterday', label: t('common.time.range.yesterday') },
  { value: 'last_7d', label: t('common.time.range.last_7d') },
])

const categoryLabel = computed(
  () => categoryOptions.value.find(opt => opt.value === modelCategory.value)?.label ?? '',
)
const timeLabel = computed(
  () => timeOptions.value.find(opt => opt.value === modelTime.value)?.label ?? '',
)

const categoryOpen = ref(false)
const timeOpen = ref(false)

const toggleCategory = () => {
  categoryOpen.value = !categoryOpen.value
  timeOpen.value = false
}
const toggleTime = () => {
  timeOpen.value = !timeOpen.value
  categoryOpen.value = false
}
const selectCategory = (value: RecordFilterModule) => {
  modelCategory.value = value
  categoryOpen.value = false
}
const selectTime = (value: RecordTimeRange) => {
  modelTime.value = value
  timeOpen.value = false
}
</script>

<style scoped lang="scss">
.filters {
  display: flex;
  gap: 10px;
  padding: 12px 14px 0;
}

.filter-dropdown {
  position: relative;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 13px;
  border-radius: var(--r-sm);
  background: var(--surface);
  border: 1px solid var(--hairline);
  color: var(--text);
  font-size: 12.5px;
  font-weight: 700;

  .chip-value {
    color: var(--primary);
  }
}

.filter-dropdown.open .filter-chip {
  border-color: var(--primary);
  color: var(--primary);
}

.chev {
  font-style: normal;
  font-size: 10px;
  color: var(--muted);
  transition: transform 0.18s;
}

.filter-dropdown.open .chev {
  transform: rotate(180deg);
}

.filter-options {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  min-width: 110px;
  padding: 6px;
  border-radius: var(--r-md);
  background: var(--surface);
  border: 1px solid var(--hairline);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  opacity: 0;
  transform: translateY(-6px);
  pointer-events: none;
  transition:
    opacity 0.16s,
    transform 0.16s;

  &.open,
  .filter-dropdown.open & {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  li {
    display: block;
    width: 100%;
    text-align: left;
    padding: 9px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
  }

  li.active {
    color: var(--primary);
    background: rgba(var(--primary-rgb), 0.12);
    font-weight: 800;
  }
}
</style>
